// CRM-native checkout modal — rep runs Stripe payment on behalf of the
// shopper without bouncing to squish.co. Used from Account detail when a
// customer reads their card over the phone (MOTO) and the rep types it in
// directly in the CRM.
//
// Flow:
//   1. Open with a cart object + shopperEmail/shopperName
//   2. Call repCheckoutIntent → get clientSecret + customerSessionClientSecret
//   3. Mount Stripe Elements; rep picks saved card OR enters new
//   4. stripe.confirmPayment → on success, repCheckoutComplete
//   5. Show success + fulfilment summary; for EMAIL plans the complete
//      response also carries nextSteps[] (setup link + script the rep reads
//      to the customer) — rendered above the fulfilment list
//
// Stripe publishable key must be on CF Pages env as
// VITE_STRIPE_PUBLISHABLE_KEY (or legacy REACT_APP_STRIPE_PUBLISHABLE_KEY)
// before first use.

import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { REP_PAYMENT_ELEMENT_OPTIONS } from '../lib/repCheckout';
import { repCheckoutIntent, repCheckoutComplete } from '../api';

const TOS_VERSION = '2026-04-19-rep';
// Allow either VITE_STRIPE_PUBLISHABLE_KEY or REACT_APP_STRIPE_PUBLISHABLE_KEY
// — vite.config.js has both prefixes in envPrefix so existing CF Pages env
// vars keep working. New environments should prefer VITE_*.
const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
  import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const stripeAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#6366f1', colorBackground: '#0f0f1a', colorText: '#ffffff',
    colorDanger: '#f87171', fontFamily: "'Inter', system-ui, sans-serif",
    borderRadius: '8px',
  },
};

export default function RepCheckoutModal({ cart, shopperEmail, shopperName, hasExistingCards, onClose, onPaid }) {
  const [phase, setPhase] = useState('review'); // review | pay | success
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(null);

  if (!stripePromise) {
    return (
      <ModalFrame onClose={onClose} title="Checkout unavailable">
        <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>
          CRM Stripe publishable key not set. Add <code style={{ fontFamily: 'var(--mono)' }}>VITE_STRIPE_PUBLISHABLE_KEY</code> to CF Pages env vars and redeploy.
        </p>
      </ModalFrame>
    );
  }

  async function startIntent() {
    setLoading(true); setError('');
    try {
      const result = await repCheckoutIntent(cart.id, {
        shopperEmail,
        shopperName,
        tosVersion: TOS_VERSION,
        tosAcceptedAt: new Date().toISOString(),
      });
      setIntent(result);
      setPhase('pay');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  return (
    <ModalFrame onClose={onClose} title={phase === 'success' ? 'Payment captured' : 'Checkout for customer'}>
      {phase === 'review' && (
        <ReviewPhase
          cart={cart}
          shopperEmail={shopperEmail}
          loading={loading}
          error={error}
          onStart={startIntent}
        />
      )}
      {phase === 'pay' && intent && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: intent.clientSecret,
            customerSessionClientSecret: intent.customerSessionClientSecret,
            appearance: stripeAppearance,
          }}
        >
          <PayPhase
            cart={cart}
            intent={intent}
            shopperEmail={shopperEmail}
            shopperName={shopperName}
            hasExistingCards={hasExistingCards}
            onPaid={(result) => { setPaid(result); setPhase('success'); onPaid?.(result); }}
          />
        </Elements>
      )}
      {phase === 'success' && paid && (
        <SuccessPhase cart={cart} paid={paid} onClose={onClose} />
      )}
    </ModalFrame>
  );
}

function ModalFrame({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14,
        width: '92%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
      }}>
        <div style={{ padding: '22px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}

function ReviewPhase({ cart, shopperEmail, loading, error, onStart }) {
  const items = cart.items || [];
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        Charging
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16, fontFamily: 'var(--mono)' }}>
        {shopperEmail}
      </div>

      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
        {items.map((i, n) => (
          <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: n < items.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
            <span style={{ color: 'var(--text)' }}>{i.productName || i.productCode} × {i.quantity || 1}</span>
            <span style={{ color: 'var(--text2)', fontFamily: 'var(--mono)' }}>${Number(i.lineTotal ?? i.unitPrice ?? 0).toFixed(2)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 4, borderTop: '1px solid var(--border)', fontWeight: 700 }}>
          <span style={{ color: 'var(--text)' }}>Total</span>
          <span style={{ color: 'var(--accent)', fontFamily: 'var(--mono)' }}>${Number(cart.total || 0).toFixed(2)}</span>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text3)', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 6, padding: 10, marginBottom: 14, lineHeight: 1.5 }}>
        <strong style={{ color: '#fbbf24' }}>MOTO notice:</strong> You're charging this card on the customer's verbal authorization over the phone.
        Confirm the amount back to them before clicking Continue. The charge is flagged as MOTO with Stripe for proper processor categorisation.
      </div>

      {error && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>⚠ {error}</div>}

      <button
        onClick={onStart}
        disabled={loading}
        style={{
          width: '100%', background: loading ? 'rgba(99,102,241,0.5)' : 'var(--accent)',
          border: 'none', borderRadius: 8, padding: '12px', color: '#fff',
          fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        }}
      >
        {loading ? 'Setting up…' : 'Continue to payment →'}
      </button>
    </div>
  );
}

function PayPhase({ cart, intent, shopperEmail, shopperName, hasExistingCards, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [makeDefault, setMakeDefault] = useState(!hasExistingCards);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function handleConfirm() {
    if (!stripe || !elements) return;
    setLoading(true); setErr('');
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
      if (error) { setErr(error.message); setLoading(false); return; }
      if (paymentIntent?.status !== 'succeeded') {
        setErr(`Payment status: ${paymentIntent?.status || 'unknown'}`);
        setLoading(false); return;
      }
      const result = await repCheckoutComplete(cart.id, {
        paymentIntentId: paymentIntent.id,
        makeDefault,
        shopperEmail,
        shopperName,
      });
      onPaid(result);
    } catch (ex) {
      setErr(ex.message || 'Something went wrong');
    }
    setLoading(false);
  }

  return (
    <div>
      {!hasExistingCards && (
        <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 6, padding: 10, marginBottom: 14, fontSize: 12, color: '#86efac', lineHeight: 1.5 }}>
          🔒 First card on file — will be saved automatically for future renewals.
        </div>
      )}
      {hasExistingCards && (
        <div style={{ background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.3)', borderRadius: 6, padding: 10, marginBottom: 14, fontSize: 12, color: '#c7d2fe', lineHeight: 1.5 }}>
          👤 Existing cards show in the picker below. Pick one, or enter a new card (new cards are saved).
        </div>
      )}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
        {/* Never the staff browser's wallets: see src/lib/repCheckout.js */}
        <PaymentElement options={REP_PAYMENT_ELEMENT_OPTIONS} />
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 14, cursor: hasExistingCards ? 'pointer' : 'default', opacity: hasExistingCards ? 1 : 0.7 }}>
        <input
          type="checkbox"
          checked={makeDefault}
          disabled={!hasExistingCards}
          onChange={e => setMakeDefault(e.target.checked)}
          style={{ marginTop: 2, accentColor: 'var(--accent)', cursor: hasExistingCards ? 'pointer' : 'default' }}
        />
        <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
          {hasExistingCards
            ? 'Set this card as default for future renewals.'
            : 'This card will be the default for renewals (required — first card on file).'}
        </span>
      </label>
      {err && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
      <button
        onClick={handleConfirm}
        disabled={loading || !stripe}
        style={{
          width: '100%', background: loading ? 'rgba(99,102,241,0.5)' : 'var(--accent)',
          border: 'none', borderRadius: 8, padding: '12px', color: '#fff',
          fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        }}
      >
        {loading ? 'Processing…' : `Charge $${Number(cart.total || 0).toFixed(2)}`}
      </button>
      <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11, marginTop: 10, fontFamily: 'var(--mono)' }}>
        MOTO · Secured by Stripe
      </p>
    </div>
  );
}

function SuccessPhase({ cart, paid, onClose }) {
  const fulfillment = paid.fulfillment || [];
  // EMAIL plans sold over the phone: the shopper has no account yet, so the
  // backend hands back a setup link + a script the rep reads to the customer
  // (sign up with the charged email, verify it, open the link).
  const nextSteps = paid.nextSteps || [];
  const [copiedId, setCopiedId] = useState(null);

  const copySetupUrl = async (step) => {
    try {
      await navigator.clipboard.writeText(step.setupUrl);
      setCopiedId(step.setupId);
      setTimeout(() => setCopiedId(id => (id === step.setupId ? null : id)), 1500);
    } catch { /* clipboard unavailable — the link is visible next to the button */ }
  };

  return (
    <div>
      <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Payment captured</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
          ${Number(cart.total || 0).toFixed(2)} charged
        </div>
      </div>
      {nextSteps.length > 0 && (
        <div style={{ background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.3)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#c7d2fe', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Next steps — read to the customer
          </div>
          {nextSteps.map((s, n) => (
            <div key={s.setupId || n} style={{ padding: n > 0 ? '10px 0 2px' : '0 0 2px', borderTop: n > 0 ? '1px solid var(--border)' : 'none', marginTop: n > 0 ? 10 : 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                {s.productName || s.productCode}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8 }}>
                {s.repScript}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px' }}>
                <span style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text2)', overflowWrap: 'anywhere' }}>
                  {s.setupUrl}
                </span>
                <button
                  onClick={() => copySetupUrl(s)}
                  title={`Copy ${s.setupUrl}`}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, flexShrink: 0, padding: 0 }}
                >
                  {copiedId === s.setupId ? '✓' : '⧉'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {fulfillment.length > 0 && (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Fulfilment
          </div>
          {fulfillment.map((f, n) => (
            <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
              <span style={{ color: 'var(--text2)' }}>{f.domain || f.productName || f.productCode}</span>
              <span style={{
                color: f.fulfilled ? '#4ade80' : f.error ? '#f87171' : '#fbbf24',
                fontFamily: 'var(--mono)', fontSize: 11,
              }}>
                {f.fulfilled ? `✓ ${f.action || 'done'}` : f.error ? '✗ ' + f.error : f.action || 'pending'}
              </span>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={onClose}
        style={{
          width: '100%', background: 'var(--accent)', border: 'none', borderRadius: 8,
          padding: '10px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Close
      </button>
    </div>
  );
}
